import { useEffect, useMemo, useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  PayrollConceptFragment,
  PayrollValueObjectsConceptCategoryEnum,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/SelectField'
import Selector from 'modules/core/components/Selector'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateConcept } from 'modules/payroll_bundle/hooks/useCreateConcept'
import { useUpdateConcept } from 'modules/payroll_bundle/hooks/useUpdateConcept'
import {
  PAYROLL_CONCEPT_CATEGORIES,
  payrollConceptCategoriesLabels,
} from 'modules/payroll_bundle/lib/constants'

interface Props {
  onClose: () => void
  onCreate: (conceptId: number) => void
  editMode: boolean
  sortedConcepts: PayrollConceptFragment[]
}

interface FormValues {
  companyId: number
  name?: string
  label: string
  description?: string
  category: PayrollValueObjectsConceptCategoryEnum
  default: boolean
}

const DEFAULT_CATEGORY = 'earnings_fixed_salary'

const ConceptModal = ({ onClose, onCreate, editMode, sortedConcepts }: Props) => {
  const { createConcept } = useCreateConcept()
  const { updateConcept } = useUpdateConcept()
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const [selectedConcept, setSelectedConcept] = useState<PayrollConceptFragment | undefined>(
    undefined
  )

  const payrollConceptCategories = payrollConceptCategoriesLabels()
  const categories = useMemo(
    () =>
      Object.entries(payrollConceptCategories).map(([value, label]) => ({
        label,
        value,
      })),
    [payrollConceptCategories]
  )

  const title = editMode
    ? i18n.t('payroll_bundle.results_payments.new_concept_modal.edit.title')
    : i18n.t('payroll_bundle.results_payments.new_concept_modal.add.title')
  const description = editMode
    ? i18n.t('payroll_bundle.results_payments.new_concept_modal.edit.description')
    : i18n.t('payroll_bundle.results_payments.new_concept_modal.add.description')

  const onCreateSubmit = async (newValues: FormValues) => {
    const result = await createConcept(newValues)
    const handled = handleResult(result, (e) => {
      setErrors(e)
      return
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('payroll_bundle.results_payments.new_concept_modal.add.success'),
      })

      const createdConcept = handled.data.concept
      if (createdConcept) {
        onCreate(createdConcept.id)

        track(trackingEvents.payroll.addNewConcept, {
          companyId,
          conceptName: createdConcept.label,
        })
      }
      onClose()
    }

    return handled
  }

  const onUpdateSubmit = async (newValues: FormValues, conceptToUpdate: PayrollConceptFragment) => {
    const result = await updateConcept({ id: conceptToUpdate.id, ...newValues })
    const handled = handleResult(result, (e) => {
      setErrors(e)
      return
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      onClose()
    }

    return handled
  }

  const { handleSubmit, errors, isSubmitting, setErrors, setValues, values } = useForm<FormValues>({
    defaultValues: {
      companyId,
      label: selectedConcept?.label ?? '',
      description: selectedConcept?.description ?? '',
      category: selectedConcept?.category ?? DEFAULT_CATEGORY,
      default: selectedConcept?.default ?? false,
    },
    onSubmit: async (newValues) => {
      return selectedConcept
        ? onUpdateSubmit(newValues, selectedConcept)
        : onCreateSubmit(newValues)
    },
  })

  useEffect(() => {
    if (selectedConcept) {
      setValues({
        companyId: selectedConcept.companyId,
        name: selectedConcept.label,
        label: selectedConcept.label,
        description: selectedConcept.description ?? '',
        category: selectedConcept.category ?? DEFAULT_CATEGORY,
        default: selectedConcept.default,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConcept])

  const footerButtons = () => {
    const isDisabled = isSubmitting || !values.label || !values.category
    const closeButton = (
      <Button type="secondary" onClick={onClose}>
        {i18n.t('common.close')}
      </Button>
    )

    if (editMode && !selectedConcept) {
      return closeButton
    }

    if (editMode) {
      return (
        <>
          <Button type="secondary" onClick={() => setSelectedConcept(undefined)}>
            {i18n.t('common.back')}
          </Button>
          {!values.default && (
            <Button disabled={isDisabled} submit>
              {i18n.t('payroll_bundle.results_payments.new_concept_modal.edit.submit')}
            </Button>
          )}
        </>
      )
    }

    return (
      <>
        {closeButton}
        <Button disabled={isDisabled} submit>
          {i18n.t('payroll_bundle.results_payments.new_concept_modal.add.submit')}
        </Button>
      </>
    )
  }

  return (
    <Modal
      illustration="document-edit"
      title={title}
      description={description}
      onClose={onClose}
      onSubmit={() => handleSubmit()}
      footer={footerButtons()}
    >
      {editMode && !selectedConcept && (
        <Selector.Container
          searchPlaceholder={i18n.t('common.select')}
          blankLabel=""
          value={[]}
          onChange={(selection) =>
            setSelectedConcept(sortedConcepts.find((concept) => concept.id === selection))
          }
        >
          {sortedConcepts.map((concept) => {
            return (
              <Selector.Option
                key={concept.id}
                value={concept.id}
                label={concept.name}
                searchValue={concept.name}
              >
                {concept.name}
              </Selector.Option>
            )
          })}
        </Selector.Container>
      )}
      {(selectedConcept || !editMode) && (
        <Box gap="s16">
          <TextField
            label={i18n.t('payroll_bundle.results_payments.new_concept_modal.form.concept')}
            value={values.name}
            onChange={(value) =>
              setValues({
                ...values,
                label: value,
                name: value,
              })
            }
            error={errors.name}
            mandatory
            readonly={values.default}
          />
          <Select
            label={i18n.t('payroll_bundle.results_payments.new_concept_modal.form.category')}
            value={values.category}
            onChange={(value) => {
              setValues({
                ...values,
                category:
                  PAYROLL_CONCEPT_CATEGORIES.find((cat) => cat === value) ?? DEFAULT_CATEGORY,
              })
            }}
            options={categories}
            mandatory
            readonly={values.default}
            error={errors.category}
          />
          <TextArea
            label={i18n.t('payroll_bundle.results_payments.new_concept_modal.form.description')}
            value={values.description}
            onChange={(value) =>
              setValues({
                ...values,
                description: value,
              })
            }
            error={errors.description}
            readonly={values.default}
          />
        </Box>
      )}
    </Modal>
  )
}

export default ConceptModal
